// client/src/components/templates/certificados/TemplateCertificadoPreview.js - VERSÃO COMPLETA CORRIGIDA
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const TemplateCertificadoPreview = ({ template, onClose }) => {
    const { setAlert } = useAlert();
    const [showPreview, setShowPreview] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageContents, setPageContents] = useState([]);
    const [processedContent, setProcessedContent] = useState('');
    const iframeRef = useRef(null);

    // Dados de exemplo para preview do certificado
    const getSampleData = () => {
        return {
            empresa: {
                nome: "ACME Educação & Consultoria Ltda",
                cnpj: "12.345.678/0001-90",
                endereco: "Rua das Flores, 123 - Centro, São Paulo - SP",
                telefone: "(11) 9999-8888",
                email: "contato@acmeeducacao.com.br"
            },
            cliente: {
                nomeComplete: "Maria da Silva Santos",
                documentoPrincipal: "123.456.789-00"
            },
            servico: {
                nome: "Curso Avançado de Desenvolvimento Web Full Stack",
                dataConclusao: "15 de Dezembro de 2024",
                cargaHoraria: "120"
            }
        };
    };

    // Função para processar o conteúdo do template com placeholders
    const processTemplateContent = (templateContent) => {
        if (!templateContent) return '';
        
        const sampleData = getSampleData();
        
        // Mapeamento de placeholders para valores de exemplo
        const placeholders = {
            // Empresa
            '{{empresa.nome}}': sampleData.empresa.nome,
            '{{empresa.cnpj}}': sampleData.empresa.cnpj,
            '{{empresa.endereco}}': sampleData.empresa.endereco,
            '{{empresa.telefone}}': sampleData.empresa.telefone,
            '{{empresa.email}}': sampleData.empresa.email,
            
            // Cliente
            '{{cliente.nomeComplete}}': sampleData.cliente.nomeComplete,
            '{{cliente.documentoPrincipal}}': sampleData.cliente.documentoPrincipal,
            
            // Serviço/Treinamento
            '{{servico.nome}}': sampleData.servico.nome,
            '{{servico.dataConclusao}}': sampleData.servico.dataConclusao,
            '{{servico.cargaHoraria}}': sampleData.servico.cargaHoraria,
            
            // Correção do placeholder typo
            '{{servico.cargalioraria}}': sampleData.servico.cargaHoraria,
            
            // Placeholder adicional encontrado no template
            '{{empresa nome}}': sampleData.empresa.nome,
            '{{empresa.org}}': sampleData.empresa.nome
        };

        let content = templateContent;

        // Aplica as substituições
        Object.entries(placeholders).forEach(([placeholder, value]) => {
            const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
            content = content.replace(regex, value);
        });

        return content;
    };

    // Função para dividir conteúdo em páginas (certificados geralmente são uma página)
    const splitContentIntoPages = (content) => {
        if (!content) return [''];
        
        // Para certificados, geralmente mantemos em uma página única
        const pages = [content];
        console.log(`📄 [DEBUG] Certificado dividido em ${pages.length} páginas`);
        return pages;
    };

    // FUNÇÃO CORRIGIDA: Criar conteúdo de página individual para preview
    const createPageContent = (pageNumber) => {
        if (!processedContent || pageContents.length === 0) {
            return createErrorContent();
        }

        const pageContent = pageContents[pageNumber - 1] || '';
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Certificado: ${template.nome} - Página ${pageNumber}</title>
                <style>
                    /* RESET E CONFIGURAÇÕES GLOBAIS */
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    
                    body {
                        font-family: 'Times New Roman', Times, serif;
                        color: #1a1a1a;
                        line-height: 1.6;
                        margin: 0;
                        padding: 0;
                        background: white;
                        width: 297mm;
                        height: 210mm;
                        overflow: hidden;
                    }
                    
                    /* CONTAINER PRINCIPAL DO CERTIFICADO */
                    .certificate-container {
                        width: 297mm;
                        height: 210mm;
                        padding: 15mm 20mm;
                        background: white;
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        border: 3px double #0b63d6;
                        margin: 0 auto;
                    }
                    
                    /* MARCA D'ÁGUA */
                    .watermark {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-45deg);
                        font-size: 80px;
                        color: rgba(11, 99, 214, 0.08);
                        font-weight: bold;
                        z-index: 0;
                        white-space: nowrap;
                        pointer-events: none;
                    }
                    
                    /* CONTEÚDO DO CERTIFICADO */
                    .certificate-content {
                        width: 100%;
                        max-width: 240mm;
                        text-align: center;
                        position: relative;
                        z-index: 1;
                    }
                    
                    /* ESTILOS ESPECÍFICOS DO TEMPLATE */
                    .company-header {
                        margin-bottom: 3rem;
                    }
                    
                    .company-header h1 {
                        font-size: 24pt;
                        color: #0b63d6;
                        margin-bottom: 0.5rem;
                        font-weight: bold;
                    }
                    
                    .company-header strong {
                        font-size: 11pt;
                        color: #666;
                    }
                    
                    .certificate-title h2 {
                        font-size: 20pt;
                        color: #2d3748;
                        margin: 2rem 0;
                        font-weight: bold;
                        text-transform: uppercase;
                    }
                    
                    .certificate-text {
                        font-size: 12pt;
                        margin: 1.5rem 0;
                        line-height: 1.8;
                    }
                    
                    .client-info {
                        margin: 2rem 0;
                    }
                    
                    .client-name {
                        font-size: 16pt;
                        font-weight: bold;
                        color: #0b63d6;
                        margin: 1rem 0;
                        padding: 0.5rem 0;
                        border-bottom: 2px solid #0b63d6;
                        display: inline-block;
                    }
                    
                    .service-info {
                        margin: 2rem 0;
                        padding: 1.5rem;
                        background: #f8f9fa;
                        border-radius: 4px;
                        font-size: 12pt;
                    }
                    
                    .service-info h3 {
                        font-size: 14pt;
                        color: #2c5530;
                        margin: 1rem 0;
                        font-style: italic;
                    }
                    
                    .recognition-message {
                        font-size: 11pt;
                        color: #666;
                        margin: 2rem 0;
                        font-style: italic;
                    }
                    
                    .certificate-footer {
                        margin-top: 3rem;
                        padding-top: 1rem;
                        border-top: 1px solid #ccc;
                        width: 100%;
                    }
                    
                    .signature-area {
                        margin-top: 3rem;
                        text-align: center;
                    }
                    
                    .signature-line {
                        border-top: 1px solid #000;
                        width: 200px;
                        margin: 2rem auto 0.5rem auto;
                    }
                    
                    .signature-name {
                        font-size: 11pt;
                        font-weight: bold;
                    }
                    
                    .signature-role {
                        font-size: 10pt;
                        color: #666;
                    }
                    
                    /* NUMERAÇÃO DE PÁGINA */
                    .page-number {
                        position: absolute;
                        bottom: 10mm;
                        right: 15mm;
                        font-size: 10pt;
                        color: #999;
                    }
                    
                    /* REGRAS DE IMPRESSÃO */
                    @media print {
                        @page {
                            size: A4 landscape;
                            margin: 0;
                        }
                        
                        body {
                            width: 297mm !important;
                            height: 210mm !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            background: white !important;
                        }
                        
                        .certificate-container {
                            border: 3px double #0b63d6 !important;
                            box-shadow: none !important;
                            page-break-inside: avoid;
                        }
                        
                        .watermark {
                            opacity: 0.08 !important;
                        }
                    }
                    
                    /* ESTILOS PARA ELEMENTOS HTML COMUNS */
                    h1, h2, h3, h4, h5, h6 {
                        text-align: center;
                        margin: 1rem 0;
                    }
                    
                    p {
                        margin: 1rem 0;
                        text-align: center;
                    }
                    
                    strong {
                        font-weight: bold;
                    }
                    
                    em {
                        font-style: italic;
                    }
                    
                    u {
                        text-decoration: underline;
                    }
                    
                    /* GARANTIR QUE O CONTEÚDO CAIBA NA PÁGINA */
                    .content-wrapper {
                        max-height: 180mm;
                        overflow: hidden;
                    }
                </style>
            </head>
            <body>
                <div class="certificate-container">
                    <div class="watermark">CERTIFICADO</div>
                    <div class="certificate-content">
                        <div class="content-wrapper">
                            ${pageContent}
                        </div>
                        <div class="signature-area">
                            <div class="signature-line"></div>
                            <div class="signature-name">[Responsável]</div>
                            <div class="signature-role">${getSampleData().empresa.nome}</div>
                        </div>
                    </div>
                    <div class="page-number">Página ${pageNumber} de ${totalPages}</div>
                </div>
            </body>
            </html>
        `;
    };

    // FUNÇÃO CORRIGIDA: Criar conteúdo completo para impressão/PDF
    const createPrintContent = () => {
        if (!processedContent || pageContents.length === 0) {
            return createErrorContent();
        }

        const pagesHTML = pageContents.map((content, index) => `
            <div class="certificate-page" style="page-break-after: ${index < pageContents.length - 1 ? 'always' : 'avoid'};">
                <div class="certificate-container">
                    <div class="watermark">CERTIFICADO</div>
                    <div class="certificate-content">
                        <div class="content-wrapper">
                            ${content}
                        </div>
                        <div class="signature-area">
                            <div class="signature-line"></div>
                            <div class="signature-name">[Responsável]</div>
                            <div class="signature-role">${getSampleData().empresa.nome}</div>
                        </div>
                    </div>
                    <div class="page-number">Página ${index + 1} de ${pageContents.length}</div>
                </div>
            </div>
        `).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Certificado: ${template.nome}</title>
                <style>
                    /* RESET PARA IMPRESSÃO */
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    
                    @page {
                        size: A4 landscape;
                        margin: 0;
                    }
                    
                    body {
                        font-family: 'Times New Roman', Times, serif;
                        color: #1a1a1a;
                        line-height: 1.6;
                        margin: 0;
                        padding: 0;
                        background: white;
                        width: 297mm;
                    }
                    
                    .certificate-page {
                        width: 297mm;
                        height: 210mm;
                        padding: 15mm 20mm;
                        background: white;
                        position: relative;
                        page-break-after: always;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    }
                    
                    .certificate-page:last-child {
                        page-break-after: avoid;
                    }
                    
                    .certificate-container {
                        border: 3px double #0b63d6;
                        width: 100%;
                        height: 100%;
                        padding: 20mm;
                        background: white;
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    }
                    
                    .watermark {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-45deg);
                        font-size: 80px;
                        color: rgba(11, 99, 214, 0.08);
                        font-weight: bold;
                        z-index: 0;
                        white-space: nowrap;
                    }
                    
                    .certificate-content {
                        width: 100%;
                        max-width: 220mm;
                        text-align: center;
                        position: relative;
                        z-index: 1;
                    }
                    
                    .content-wrapper {
                        max-height: 120mm;
                        overflow: hidden;
                    }
                    
                    .page-number {
                        position: absolute;
                        bottom: 10mm;
                        right: 15mm;
                        font-size: 10pt;
                        color: #999;
                    }
                    
                    .signature-area {
                        margin-top: 2rem;
                        text-align: center;
                    }
                    
                    .signature-line {
                        border-top: 1px solid #000;
                        width: 200px;
                        margin: 1rem auto 0.5rem auto;
                    }
                </style>
            </head>
            <body>
                ${pagesHTML}
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo de erro
    const createErrorContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Certificado: ${template?.nome || 'N/A'}</title>
                <style>
                    @page { 
                        size: A4 landscape; 
                        margin: 0; 
                    }
                    body { 
                        font-family: Arial, sans-serif; 
                        line-height: 1.6; 
                        margin: 0; 
                        padding: 40px;
                        color: #333;
                        background: #f9f9f9;
                        width: 297mm;
                        height: 210mm;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    .error-container { 
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        padding: 40px;
                        margin: 0 auto;
                        max-width: 400px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                        text-align: center;
                    }
                    .error-title { 
                        color: #d32f2f; 
                        font-size: 20px; 
                        margin-bottom: 15px;
                    }
                    .error-message {
                        margin-bottom: 20px;
                        color: #666;
                    }
                    .support-message {
                        margin-top: 20px;
                        color: #888;
                        font-style: italic;
                    }
                </style>
            </head>
            <body>
                <div class="error-container">
                    <h1 class="error-title">Certificado Sem Conteúdo</h1>
                    <p class="error-message">Este certificado não possui conteúdo para visualização.</p>
                    <p class="support-message">Adicione conteúdo ao certificado para visualizar a prévia.</p>
                </div>
            </body>
            </html>
        `;
    };

    // Funções de navegação de página
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Funções de ação
    const handlePrint = () => {
        setIsLoading(true);
        const printContent = createPrintContent();
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
            setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
            setIsLoading(false);
            return;
        }
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Aguarda o conteúdo carregar completamente antes de imprimir
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            setIsLoading(false);
        };
        
        // Fallback caso onload não funcione
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setIsLoading(false);
        }, 1000);
    };

    const handleDownload = () => {
        setIsLoading(true);
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificado-${template.nome.replace(/\s+/g, '-').toLowerCase()}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    // Processar conteúdo do template e dividir em páginas
    useEffect(() => {
        if (template?.conteudo) {
            setIsLoading(true);
            try {
                // Processa o template substituindo placeholders
                const processed = processTemplateContent(template.conteudo);
                setProcessedContent(processed);
                
                // Divide em páginas
                const pages = splitContentIntoPages(processed);
                setPageContents(pages);
                setTotalPages(pages.length);
                setCurrentPage(1);
                
                console.log(`📄 [DEBUG] Certificado processado: ${pages.length} páginas criadas`);
            } catch (error) {
                console.error('❌ [DEBUG] Erro ao processar certificado:', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            setProcessedContent('');
            setPageContents([]);
            setTotalPages(1);
            setCurrentPage(1);
        }
    }, [template]);

    if (!template) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhum certificado selecionado para visualização</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content-panel">
            {/* CABEÇALHO NO ESTILO DO FORMULÁRIO */}
            <div className="form-header">
                <h3 className="form-title">Visualizando: {template.nome}</h3>
                <button 
                    type="button" 
                    onClick={onClose} 
                    title="Fechar" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {/* RESUMO DO CERTIFICADO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Resumo do Certificado</legend>
                <div className="form-grid-container-auto-fit">
                    <div className="form-group">
                        <label className="form-label">Nome:</label>
                        <div className="form-input form-input-disabled">{template.nome}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.ativo ? 'badge-green' : 'badge-gray'}`}>
                                {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Caracteres:</label>
                        <div className="form-input form-input-disabled highlight">
                            {template.conteudo?.length || 0}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Páginas:</label>
                        <div className="form-input form-input-disabled highlight">
                            {totalPages}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Modificado em:</label>
                        <div className="form-input form-input-disabled">
                            {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString('pt-BR') : '-'}
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* CONTROLES DE VISUALIZAÇÃO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="editor-toolbar">
                    <button 
                        onClick={togglePreview}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>
                    
                    <button 
                        onClick={handleDownload}
                        className="action-button action-button-secondary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                    
                    <button 
                        onClick={handlePrint}
                        className="action-button action-button-primary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        Imprimir/PDF
                    </button>
                </div>
            </fieldset>

            {/* PREVIEW DO CONTEÚDO - CORRIGIDO */}
            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview do Certificado - Página {currentPage} de {totalPages}
                    </legend>
                    
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 size={48} className="animate-spin text-blue-500" />
                            <p>Processando certificado...</p>
                        </div>
                    ) : !template.conteudo ? (
                        <div className="panel-state-wrapper error">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <h4>Certificado Sem Conteúdo</h4>
                            <p>Este certificado não possui conteúdo para visualização.</p>
                        </div>
                    ) : pageContents.length > 0 ? (
                        <div className="a4-preview-wrapper certificado-mode">
                            {/* CONTROLES DE PAGINAÇÃO */}
                            <div className="editor-toolbar" style={{ marginBottom: '1rem' }}>
                                <button 
                                    onClick={prevPage}
                                    disabled={currentPage === 1}
                                    className="action-button action-button-secondary"
                                >
                                    <ChevronLeft size={16} />
                                    Anterior
                                </button>
                                
                                <span className="pagination-info" style={{ 
                                    padding: '0.5rem 1rem', 
                                    fontSize: '0.875rem',
                                    color: '#6b7280'
                                }}>
                                    Página {currentPage} de {totalPages}
                                </span>
                                
                                <button 
                                    onClick={nextPage}
                                    disabled={currentPage === totalPages}
                                    className="action-button action-button-secondary"
                                >
                                    Próxima
                                    <ChevronRight size={16} />
                                </button>
                            </div>

                            {/* CONTAINER DO CERTIFICADO EM PAISAGEM - CORRIGIDO */}
                            <div className="single-page-container certificado-container">
                                <div 
                                    className="a4-page-single certificado-page"
                                    style={{ 
                                        width: '100%',
                                        maxWidth: '297mm',
                                        height: '210mm',
                                        aspectRatio: '297 / 210'
                                    }}
                                >
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={createPageContent(currentPage)}
                                        title={`Certificado: ${template.nome} - Página ${currentPage}`}
                                        className="preview-iframe-single certificado-iframe"
                                        sandbox="allow-same-origin"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            background: 'white'
                                        }}
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="panel-state-wrapper">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <p>Certificado não disponível para visualização</p>
                        </div>
                    )}
                </fieldset>
            )}

            {/* BOTÕES DE AÇÃO NO FINAL */}
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={onClose}
                    className="action-button action-button-secondary"
                >
                    <ArrowLeft size={18} />
                    Voltar para Lista
                </button>
            </div>
        </div>
    );
};

export default TemplateCertificadoPreview;